import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import QuotationTenderingContext from "../QuotationTenderingContext";
import Layout from "../Layout";
import { Button, TextBox } from "../../../../components";
import useQuotation from "../../../../hooks/useQuotation";
import { ObjectId } from "bson";
import { toast } from "react-toastify";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import DocumentUploader from "../../../../components/common/DocumentUploader";
import { format } from "date-fns";
import AppContext from "../../../../AppContext";
import TenderingDetailContext from "../../context";
import useUsers from "../../../../hooks/useUsers";
import {
  formatNumber,
  numberToOrdinal,
} from "../../../../helpers/common.helper";
import { Form, Formik } from "formik";
import TextArea from "../../../../components/common/TextArea";
import useTendering from "../../../../hooks/useTendering";

const assetsUri = import.meta.env.ASSETS_URI;

const TABS = {
  cover: 1,
  details: 2,
  paymentTerms: 3,
};

const Summary = (props) => {
  const { setStep, details, goBack } = useContext(QuotationTenderingContext);
  const { user } = useContext(AppContext);
  const { details: tenderingDetail } = useContext(TenderingDetailContext);
  const { getUser } = useUsers();
  const navigate = useNavigate();
  const { getSignedUrl } = useTendering();
  const { createQuotation, getPDF, getQuotationCode, patchQuotation } =
    useQuotation();
  const [logoFile, setLogoFile] = useState(undefined);
  const [logoPreview, setLogoPreview] = useState(undefined);
  const [currentTab, setCurrentTab] = useState(TABS.cover);
  const [owner, setOwner] = useState(undefined);
  const [days, setDays] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [sanitizedData, setSanitizedData] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setStep(3);

    if (!details) return;

    getUser(tenderingDetail.createdBy).then((data) => {
      // debugger;
      setOwner({
        name: `${data.firstname} ${data.surname ?? ""}`,
        address: `${data.contact.address.streetAddress} ${data.contact.address.apt} ${data.contact.address.subDistrict} ${data.contact.address.district} ${data.contact.address.province} ${data.contact.address.postalCode}`,
      });
    });

    getQuotationCode().then((data) => {
      const sanitizedData = _getSanitizedData();
      setSanitizedData({ ...sanitizedData, code: data });
    });
  }, []);

  const roundNumber = (num) => {
    return Number(Number(num).toFixed(2));
  };

  const _getSanitizedData = () => {
    const {
      _id,
      payment: { advancedPayment, paymentTerms: subjectPaymentTerms, wht },
      subjects,
      areas,
    } = details;

    const _areas = areas.map((a) => ({
      _id: new ObjectId(),
      name: a,
    }));

    let expectedGrandTotal = 0;

    const _subjects = subjects.map((subject, sIndex) => {
      expectedGrandTotal += subject.price;

      return {
        _id: new ObjectId(),
        name: subject.name,
        items: subject.items.map((item, itemIndex) => {
          const _itemAreas = [];

          for (let areaIndex = 0; areaIndex < item.areas.length; areaIndex++) {
            const area = item.areas[areaIndex];

            if (!area) continue;

            _itemAreas.push({
              area: _areas[areaIndex]._id,
              size: area,
            });
          }

          return {
            _id: new ObjectId(),
            name: item.name,
            symbol: item.symbol,
            areas: _itemAreas,
            size: {
              unit: item.unit,
            },
            material: item.material,
            labor: item.labor,
            price: item.price,
          };
        }),
        price: subject.price,
      };
    });

    const _paymentTerms = Array.from(
      { length: subjectPaymentTerms[0].payments.length },
      (_, i) => ({
        subjects: _subjects.map((s) => ({
          subject: s._id,
          items: s.items.map((i) => ({
            item: i._id,
            percentage: 0,
            total: 0,
          })),
          subTotal: 0,
        })),
        subTotal: 0,
      })
    );

    for (
      let subjectIndex = 0;
      subjectIndex < subjectPaymentTerms.length;
      subjectIndex++
    ) {
      const subjectPaymentTerm = subjectPaymentTerms[subjectIndex];

      for (
        let itemIndex = 0;
        itemIndex < subjectPaymentTerm.items.length;
        itemIndex++
      ) {
        const itemPaymentTerm = subjectPaymentTerm.items[itemIndex];

        const payments = itemPaymentTerm.payments;

        for (let pIndex = 0; pIndex < payments.length; pIndex++) {
          const payment = _.cloneDeep(payments[pIndex]);

          _paymentTerms[pIndex].subjects[subjectIndex].items[
            itemIndex
          ].percentage = payment.percentage;
          _paymentTerms[pIndex].subjects[subjectIndex].items[itemIndex].total =
            payment.total;

          _paymentTerms[pIndex].subjects[subjectIndex].subTotal +=
            payment.total;
          _paymentTerms[pIndex].subTotal += payment.total;
        }
      }
    }

    const body = {
      code: "A",
      tendering: _id,
      areas: _areas,
      subjects: _subjects,
      advancedPayment,
      paymentTerms: _paymentTerms,
      netTotal: expectedGrandTotal,
      vat: roundNumber(expectedGrandTotal * 0.07),
      wht: wht ? roundNumber(expectedGrandTotal * 1.07 * 0.03 * -1) : undefined,
      grandTotal: roundNumber(
        wht ? expectedGrandTotal * 1.07 * 0.97 : expectedGrandTotal * 1.07
      ),
    };

    return body;
  };

  const _uploadFile = async (key, file) => {
    const signedUrl = await getSignedUrl(key, file.type);

    const uploadResponse = await fetch(signedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: 0,
      },
      body: file,
    });

    if (uploadResponse.status !== 200) {
      return false;
    }

    return true;
  };

  const handleSend = async () => {
    // * Upload logo file
    if (!logoFile) {
      toast.error("Logo file not found");
      return;
    }

    const { _id } = details;

    setSubmitting(true);

    const logoId = new ObjectId();
    const logoKey = `tenderings/${_id}/quotations/logos/${logoId}`;

    if (!(await _uploadFile(logoKey, logoFile))) {
      toast.error("Unable to upload logo file");
      setSubmitting(false);
      return;
    }

    let _body = {
      ...sanitizedData,
      logo: {
        _id: logoId,
        key: logoKey,
        name: logoFile.name,
        mimeType: logoFile.type,
        size: logoFile.size,
      },
      active: false,
    };

    if (days) {
      _body.days = days;
    }

    if (remarks) {
      _body.remarks = remarks;
    }

    try {
      const _createResult = await createQuotation(_body);

      const pdfId = new ObjectId();
      const pdfKey = `quotations/${_createResult._id}/files/${pdfId}`;

      // * Get PDF to save
      const _data = {
        ..._createResult,
        logo: `${assetsUri}/${logoKey}`,
        date: format(new Date(), "dd MMM yyyy"),
        appraiser: `${user?.firstname} ${user?.surname}`,
        owner: {
          name: owner.name,
          address: owner.address,
        },
        projectName: tenderingDetail?.name,
      };

      const pdf = await getPDF("quotation-construction-report", _data);

      const pdfFile = new File(
        [new Blob([pdf.data])],
        `${_createResult.code}.pdf`
      );

      if (!(await _uploadFile(pdfKey, pdfFile))) {
        toast.error("Unable generate PDF file");
        setSubmitting(false);
        return;
      }

      await patchQuotation(_createResult._id, {
        active: true,
        file: {
          _id: pdfId,
          key: pdfKey,
          name: pdfFile.name,
          mimeType: pdfFile.type,
          size: pdfFile.size,
        },
      });

      toast.success("Send quotation successfully!");

      navigate(`/tenderings/${_id}`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to send quotation");
    } finally {
      setSubmitting(false);
    }
  };

  const _renderPreview = () => {
    if (currentTab === TABS.cover) {
      const subTotal = details.subjects.reduce((prev, curr) => {
        return prev + curr.price;
      }, 0);

      const getCalculatedTotal = (number) => {
        let _total = number * 1.07;

        if (!details.payment.wht) {
          return _total;
        }

        return _total * 0.97;
      };

      return (
        <div className="tendering-quotation__construction__summary__preview__cover">
          <table className="tendering-quotation__construction__summary__preview__cover__table">
            <thead>
              <tr>
                <th colSpan={"100%"}>Preliminary Price Summary</th>
              </tr>
              <tr>
                <th colSpan={2}>Description</th>
                <th style={{ width: 200 }}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {details.subjects.map((subject, sIdx) => {
                return (
                  <tr>
                    <td style={{ width: 0 }}>{sIdx + 1}</td>
                    <td>{subject.name}</td>
                    <td style={{ textAlign: "right", fontWeight: 500 }}>
                      {formatNumber(subject.price)}
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td colSpan={2} style={{ textAlign: "right" }}>
                  Sub Total
                </td>
                <td style={{ textAlign: "right", fontWeight: 500 }}>
                  {formatNumber(subTotal)}
                </td>
              </tr>
              <tr>
                <td colSpan={2} style={{ textAlign: "right" }}>
                  VAT (7%)
                </td>
                <td style={{ textAlign: "right", fontWeight: 500 }}>
                  {formatNumber(subTotal * 0.07)}
                </td>
              </tr>
              {details.payment.wht && (
                <tr>
                  <td colSpan={2} style={{ textAlign: "right" }}>
                    WHT (-3%)
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 500 }}>
                    -{formatNumber(subTotal * 1.07 * 0.03)}
                  </td>
                </tr>
              )}
              <tr>
                <td colSpan={2} style={{ textAlign: "right" }}>
                  Grand Total
                </td>
                <td style={{ textAlign: "right", fontWeight: 500 }}>
                  {formatNumber(getCalculatedTotal(subTotal))}
                </td>
              </tr>
            </tbody>
          </table>
          <table className="tendering-quotation__construction__summary__preview__cover__table">
            <thead>
              <tr>
                <th colSpan={"100%"}>Payment Terms</th>
              </tr>
              <tr>
                <th colSpan={2}>Description</th>
                <th style={{ width: 200 }}>Cost</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ width: 0 }}>1</td>
                <td>Advanced Payment</td>
                <td style={{ textAlign: "right", fontWeight: 500 }}>
                  {formatNumber(
                    getCalculatedTotal(details.payment.advancedPayment.total)
                  )}
                </td>
              </tr>
              {sanitizedData?.paymentTerms.map((pt, ptIdx) => {
                const _isFinal =
                  ptIdx + 1 === sanitizedData?.paymentTerms.length;

                return (
                  <tr>
                    <td style={{ width: 0 }}>{ptIdx + 2}</td>
                    <td>
                      {_isFinal ? "Final" : numberToOrdinal(ptIdx + 1)} Payment
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 500 }}>
                      {formatNumber(getCalculatedTotal(pt.subTotal))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Formik
            initialValues={{ days, remarks }}
            validate={({ days, remarks }) => {
              setDays(days);
              setRemarks(remarks);
            }}
          >
            <Form className="tendering-quotation__construction__summary__preview__cover__additional">
              <TextBox name="days" label={"Days of work"} type="number" />
              <TextArea name="remarks" label={"Remark"} />
            </Form>
          </Formik>
        </div>
      );
    }

    if (currentTab === TABS.details) {
      return (
        <div className="tendering-quotation__construction__summary__preview__details">
          <table className="tendering-quotation__construction__summary__preview__details__table">
            <thead>
              <tr>
                <th rowSpan={2}>No</th>
                <th rowSpan={2}>Description</th>
                <th rowSpan={2}>Symbol</th>
                <th colSpan={details.areas.length + 1}>
                  Quantity in each area
                </th>
                <th rowSpan={2}>Unit</th>
                <th rowSpan={2}>Material Cost</th>
                <th rowSpan={2}>Labor Cost</th>
                <th rowSpan={2}>Total Cost</th>
                <th rowSpan={2}>Remark</th>
              </tr>
              <tr>
                {details.areas.map((area) => (
                  <th>{area}</th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {details.subjects.map((subject) => {
                return (
                  <>
                    <tr>
                      <td colSpan={"100%"}>{subject.name}</td>
                    </tr>
                    {subject.items.map((item, iIdx) => {
                      return (
                        <tr>
                          <td>{iIdx + 1}</td>
                          <td>{item.name}</td>
                          <td>{item.symbol}</td>
                          {item.areas.map((itemArea) => {
                            return (
                              <td>{itemArea ? formatNumber(itemArea) : ""}</td>
                            );
                          })}
                          <td>{formatNumber(_.sum(item.areas))}</td>
                          <td>{item.unit}</td>
                          <td>
                            {item.material ? formatNumber(item.material) : ""}
                          </td>
                          <td>{item.labor ? formatNumber(item.labor) : ""}</td>
                          <td>{formatNumber(item.price)}</td>
                          <td>{item.remark}</td>
                        </tr>
                      );
                    })}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    if (currentTab === TABS.paymentTerms) {
      return (
        <div className="tendering-quotation__construction__summary__preview__payment-terms">
          <table className="tendering-quotation__construction__summary__preview__payment-terms__table">
            <thead>
              <tr>
                <th style={{ width: 0 }}>No</th>
                <th>Description</th>
                <th style={{ width: 140 }}>Percentage (%)</th>
                <th style={{ width: 140 }}>Price (THB)</th>
              </tr>
            </thead>
            <tbody>
              <tr className="tendering-quotation__construction__summary__preview__payment-terms__table__row__term">
                <td>1</td>
                <td>Advanced Payment</td>
                <td />
                <td>{formatNumber(sanitizedData.advancedPayment.total)}</td>
              </tr>
              {sanitizedData.paymentTerms.map((pt, ptIdx) => {
                const no = ptIdx + 2;
                const termNo = ptIdx + 1;

                const _isFinal =
                  ptIdx + 1 === sanitizedData?.paymentTerms.length;

                return (
                  <>
                    <tr className="tendering-quotation__construction__summary__preview__payment-terms__table__row__term">
                      <td>{no}</td>
                      <td>
                        {_isFinal ? "Final" : numberToOrdinal(termNo)} Payment
                      </td>
                      <td />
                      <td>{formatNumber(pt.subTotal)}</td>
                    </tr>
                    {pt.subjects.map((subject, subjectIdx) => {
                      const _subjectMatched = sanitizedData.subjects.find(
                        (s) => s._id.toString() === subject.subject.toString()
                      );

                      return (
                        <>
                          <tr className="tendering-quotation__construction__summary__preview__payment-terms__table__row__subject">
                            <td>{`${no}.${subjectIdx + 1}`}</td>
                            <td>{_subjectMatched?.name}</td>
                            <td />
                            <td>{formatNumber(subject.subTotal)}</td>
                          </tr>
                          {subject.items.map((item) => {
                            const _itemMatched = _subjectMatched.items.find(
                              (i) => i._id.toString() === item.item.toString()
                            );

                            return (
                              <tr>
                                <td />
                                <td>{_itemMatched?.name}</td>
                                <td>{item.percentage}%</td>
                                <td>{formatNumber(item.total)}</td>
                              </tr>
                            );
                          })}
                        </>
                      );
                    })}
                  </>
                );
              })}
            </tbody>
          </table>
          <table className="tendering-quotation__construction__summary__preview__payment-terms__summary">
            <tr>
              <td className="tendering-quotation__construction__summary__preview__payment-terms__summary__title">
                Net Total
              </td>
              <td className="tendering-quotation__construction__summary__preview__payment-terms__summary__value">
                {formatNumber(sanitizedData.netTotal)}
              </td>
            </tr>
            <tr>
              <td className="tendering-quotation__construction__summary__preview__payment-terms__summary__title">
                VAT (7%)
              </td>
              <td className="tendering-quotation__construction__summary__preview__payment-terms__summary__value">
                +{formatNumber(sanitizedData.vat)}
              </td>
            </tr>
            {sanitizedData.wht && (
              <tr>
                <td className="tendering-quotation__construction__summary__preview__payment-terms__summary__title">
                  WHT (3%)
                </td>
                <td className="tendering-quotation__construction__summary__preview__payment-terms__summary__value">
                  -{formatNumber(sanitizedData.wht)}
                </td>
              </tr>
            )}
            <tr>
              <td className="tendering-quotation__construction__summary__preview__payment-terms__summary__title">
                Grand Total
              </td>
              <td className="tendering-quotation__construction__summary__preview__payment-terms__summary__value">
                {formatNumber(sanitizedData.grandTotal)}
              </td>
            </tr>
          </table>
        </div>
      );
    }

    return <></>;
  };

  const _getHeaderKeyValue = (title, value) => {
    return (
      <div className="tendering-quotation__construction__summary__preview__header__text">
        <span className="tendering-quotation__construction__summary__preview__header__text__title">
          {title}
        </span>
        <span className="tendering-quotation__construction__summary__preview__header__text__value">
          {value}
        </span>
      </div>
    );
  };

  const downloadPDF = async () => {
    await toast.promise(
      new Promise(async (resolve, reject) => {
        const _data = {
          ...sanitizedData,
          logo: logoPreview,
          date: format(new Date(), "dd MMM yyyy"),
          appraiser: `${user?.firstname} ${user?.surname}`,
          owner: {
            name: owner.name,
            address: owner.address,
          },
          projectName: tenderingDetail?.name,
        };

        getPDF("quotation-construction-report", _data)
          .then((response) => {
            const blob = new Blob([response.data]);

            // Create a temporary download link
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);

            let fileName = `(Draft) ${sanitizedData.code}.pdf`;

            link.download = fileName;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            link.remove();
            window.URL.revokeObjectURL(link.href);

            resolve();
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      }),
      {
        pending: "Downloading file",
        success: "Quotation downloaded",
        error: "Failed to download",
      }
    );
  };

  if (!sanitizedData) return <></>;

  return (
    <Layout>
      <Layout.Header>
        <div className="tendering-quotation__construction__summary__header">
          <h3>Summary</h3>
          <h4>Logo</h4>
          <div className="tendering-quotation__construction__summary__header__logo">
            <DocumentUploader
              name="logo"
              accept="image/*"
              multiple={false}
              onChange={(files) => {
                if (!files || files.length === 0) {
                  setLogoFile(undefined);
                  setLogoPreview(undefined);
                  return;
                }

                setLogoFile(files.at(-1));

                const fr = new FileReader();
                fr.onload = function () {
                  setLogoPreview(fr.result);
                };
                fr.readAsDataURL(files.at(-1));
              }}
            />
            {logoPreview && <img src={logoPreview} height={100} />}
          </div>
        </div>
        <div
          style={{
            alignSelf: "flex-start",
            height: "41px",
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <Button
            label="Export Draft"
            variant="secondary"
            size="small"
            onClick={() => {
              downloadPDF();
            }}
          />
        </div>
      </Layout.Header>
      <Layout.Content>
        <div className="tendering-quotation__construction__summary__preview">
          <div className="tendering-quotation__construction__summary__preview__tabs">
            <span
              className={`tendering-quotation__construction__summary__preview__tabs__tab ${
                currentTab === TABS.cover
                  ? "tendering-quotation__construction__summary__preview__tabs__tab--active"
                  : ""
              }`}
              onClick={() => {
                setCurrentTab(TABS.cover);
              }}
            >
              Cover
            </span>
            <span
              className={`tendering-quotation__construction__summary__preview__tabs__tab ${
                currentTab === TABS.details
                  ? "tendering-quotation__construction__summary__preview__tabs__tab--active"
                  : ""
              }`}
              onClick={() => {
                setCurrentTab(TABS.details);
              }}
            >
              Details
            </span>
            <span
              className={`tendering-quotation__construction__summary__preview__tabs__tab ${
                currentTab === TABS.paymentTerms
                  ? "tendering-quotation__construction__summary__preview__tabs__tab--active"
                  : ""
              }`}
              onClick={() => {
                setCurrentTab(TABS.paymentTerms);
              }}
            >
              Payment Terms
            </span>
          </div>
          <div className="tendering-quotation__construction__summary__preview__header">
            <div className="tendering-quotation__construction__summary__preview__header__left">
              {logoPreview && (
                <img
                  className="tendering-quotation__construction__summary__preview__header__logo"
                  src={logoPreview}
                />
              )}
              {_getHeaderKeyValue("Quotation No:", sanitizedData.code)}
              {_getHeaderKeyValue(
                "Quotation Date:",
                format(new Date(), "dd MMM yyyy")
              )}
              {_getHeaderKeyValue(
                "Appraisers",
                `${user?.firstname} ${user?.surname}`
              )}
            </div>
            <div className="tendering-quotation__construction__summary__preview__header__right">
              {owner && (
                <>
                  {_getHeaderKeyValue("Project owner:", owner.name)}
                  {_getHeaderKeyValue("Address:", owner.address)}
                </>
              )}
              {_getHeaderKeyValue("Project name:", tenderingDetail?.name)}
            </div>
          </div>
          {_renderPreview()}
        </div>
      </Layout.Content>
      <Layout.Footer>
        <Button label="Return" variant={"secondary"} onClick={goBack} />
        <Button
          label="Send Quotation"
          onClick={handleSend}
          disabled={submitting}
        />
      </Layout.Footer>
    </Layout>
  );
};

Summary.propTypes = {};

export default Summary;
